import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const API_BASE = process.env.REACT_APP_API_URL || '';

const CATEGORY_COLORS = {
    'שקוף פשוט': 'bg-sky-100 text-sky-700 border-sky-200',
    'מחוסם': 'bg-blue-100 text-blue-700 border-blue-200',
    'אנטיסן': 'bg-teal-100 text-teal-700 border-teal-200',
    'טריפלקס': 'bg-emerald-100 text-emerald-700 border-emerald-200',
    'בידודית': 'bg-amber-100 text-amber-700 border-amber-200',
    'רפלקטיבית': 'bg-orange-100 text-orange-700 border-orange-200',
    'רפפה': 'bg-lime-100 text-lime-700 border-lime-200',
    'מראה': 'bg-pink-100 text-pink-700 border-pink-200',
    'פרספקס / פלקליר': 'bg-indigo-100 text-indigo-700 border-indigo-200',
    'פוליקרבונט': 'bg-cyan-100 text-cyan-700 border-cyan-200',
    'מיוחד': 'bg-slate-100 text-slate-700 border-slate-200',
};

export default function GlassCatalogPage() {
    const { currentUser } = useAuth();
    const isAdmin = currentUser?.role === 'admin';

    const [items, setItems] = useState([]);
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedCat, setSelectedCat] = useState('all');
    const [searchQuery, setSearchQuery] = useState('');

    // CRUD modal
    const [showModal, setShowModal] = useState(false);
    const [editingItem, setEditingItem] = useState(null);
    const [formData, setFormData] = useState({ glassId: '', description: '', priceSqM: '', thickness: '' });
    const [saving, setSaving] = useState(false);

    // Inline price editing
    const [editingPrice, setEditingPrice] = useState(null);
    const [priceValue, setPriceValue] = useState('');

    const token = localStorage.getItem('pro_token') || sessionStorage.getItem('pro_token') || localStorage.getItem('admin_token') || sessionStorage.getItem('admin_token');
    const headers = useMemo(() => ({
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {})
    }), [token]);

    const fetchCategories = useCallback(() => {
        fetch(`${API_BASE}/api/glass-catalog/categories`, { headers })
            .then(r => r.json())
            .then(data => setCategories(Array.isArray(data) ? data : []))
            .catch(console.error);
    }, [headers]);

    const fetchItems = useCallback(() => {
        setLoading(true);
        const params = new URLSearchParams();
        if (selectedCat !== 'all') params.set('category', selectedCat);
        if (searchQuery) params.set('search', searchQuery);

        fetch(`${API_BASE}/api/glass-catalog?${params}`, { headers })
            .then(r => r.json())
            .then(data => { setItems(Array.isArray(data) ? data : []); setLoading(false); })
            .catch(() => setLoading(false));
    }, [selectedCat, searchQuery, headers]);

    useEffect(() => { fetchCategories(); }, [fetchCategories]);
    useEffect(() => { fetchItems(); }, [fetchItems]);

    // CRUD handlers
    const openAddModal = () => {
        setEditingItem(null);
        setFormData({ glassId: '', description: '', priceSqM: '', thickness: '' });
        setShowModal(true);
    };
    const openEditModal = (item) => {
        setEditingItem(item);
        setFormData({ glassId: item.glassId, description: item.description, priceSqM: item.priceSqM || '', thickness: item.thickness || '' });
        setShowModal(true);
    };
    const handleSave = async () => {
        setSaving(true);
        try {
            const url = editingItem ? `${API_BASE}/api/glass-catalog/${editingItem._id}` : `${API_BASE}/api/glass-catalog`;
            const method = editingItem ? 'PUT' : 'POST';
            const body = { ...formData, priceSqM: parseFloat(formData.priceSqM) || 0, thickness: parseFloat(formData.thickness) || 0 };
            if (!editingItem) body.glassId = parseInt(formData.glassId) || undefined;
            const res = await fetch(url, { method, headers, body: JSON.stringify(body) });
            const data = await res.json();
            if (!res.ok) throw new Error(data.error || 'שגיאה');
            setShowModal(false);
            fetchItems();
            fetchCategories();
        } catch (err) { alert(err.message); }
        setSaving(false);
    };
    const handleDelete = async (item) => {
        if (!window.confirm(`למחוק ${item.description}?`)) return;
        try {
            const res = await fetch(`${API_BASE}/api/glass-catalog/${item._id}`, { method: 'DELETE', headers });
            if (!res.ok) { const d = await res.json(); throw new Error(d.error); }
            fetchItems();
            fetchCategories();
        } catch (err) { alert(err.message); }
    };

    // Inline price save to DB
    const savePriceToDB = async (item) => {
        try {
            const res = await fetch(`${API_BASE}/api/glass-catalog/${item._id}/price`, {
                method: 'PUT', headers,
                body: JSON.stringify({ priceSqM: parseFloat(priceValue) || 0 })
            });
            if (!res.ok) throw new Error('שגיאה בעדכון');
            setEditingPrice(null);
            fetchItems();
        } catch (err) { alert(err.message); }
    };

    const canEdit = (item) => isAdmin || (item.source === 'user' && item.createdBy?._id === currentUser?.id);

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100" dir="rtl">
            {/* Header */}
            <div className="bg-white border-b border-slate-200 sticky top-[72px] lg:top-[88px] z-40 shadow-sm">
                <div className="max-w-7xl mx-auto px-6 py-6">
                    <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                        <div>
                            <Link to="/professionals" className="inline-flex items-center gap-2 text-slate-500 hover:text-cyan-600 mb-2 transition-colors font-bold text-sm">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" /></svg>
                                חזרה לכלים
                            </Link>
                            <h1 className="text-3xl md:text-4xl font-black text-slate-900 tracking-tight">קטלוג זכוכית</h1>
                            <p className="text-slate-500 mt-1">{items.length} סוגי זכוכית • מחירים למ"ר</p>
                        </div>
                        <div className="flex gap-3 items-center flex-wrap">
                            <div className="relative">
                                <span className="absolute inset-y-0 right-0 flex items-center pr-3 text-slate-400">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
                                </span>
                                <input type="text" placeholder="חיפוש..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
                                    className="w-full md:w-64 pl-4 pr-10 py-2.5 rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:ring-2 focus:ring-cyan-500/20 focus:border-cyan-500 outline-none transition-all shadow-sm font-medium" />
                            </div>
                            <button onClick={openAddModal} className="flex items-center gap-2 bg-cyan-600 hover:bg-cyan-700 text-white px-4 py-2.5 rounded-xl font-bold text-sm transition-all shadow-md whitespace-nowrap">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" /></svg>
                                הוסף סוג
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <div className="max-w-7xl mx-auto px-6 py-8">
                {/* Category Chips */}
                <div className="flex flex-wrap gap-2 mb-6">
                    <button onClick={() => setSelectedCat('all')}
                        className={`px-4 py-1.5 rounded-full text-sm font-bold transition-all border ${selectedCat === 'all' ? 'bg-slate-800 text-white border-slate-800' : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'}`}>
                        הכל ({items.length || '...'})
                    </button>
                    {categories.map(cat => (
                        <button key={cat.category} onClick={() => setSelectedCat(cat.category)}
                            className={`px-4 py-1.5 rounded-full text-sm font-bold transition-all border ${selectedCat === cat.category
                                ? 'bg-slate-800 text-white border-slate-800'
                                : (CATEGORY_COLORS[cat.category] || 'bg-slate-100 text-slate-600 border-slate-200') + ' hover:opacity-80'
                            }`}>
                            {cat.category} ({cat.count})
                        </button>
                    ))}
                </div>

                {loading ? (
                    <div className="text-center py-20">
                        <div className="inline-block w-10 h-10 border-4 border-cyan-500 border-t-transparent rounded-full animate-spin" />
                        <p className="text-slate-500 mt-4 font-bold">טוען סוגי זכוכית...</p>
                    </div>
                ) : items.length === 0 ? (
                    <div className="text-center py-20 bg-white rounded-2xl border border-slate-200 shadow-sm">
                        <h3 className="text-xl font-bold text-slate-700">לא נמצאו סוגי זכוכית</h3>
                    </div>
                ) : (
                    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                        <div className="overflow-x-auto">
                            <table className="w-full text-right">
                                <thead>
                                    <tr className="bg-slate-50 border-b border-slate-200">
                                        <th className="p-3 text-xs font-bold text-slate-500 w-16">קוד</th>
                                        <th className="p-3 text-xs font-bold text-slate-500">תיאור</th>
                                        <th className="p-3 text-xs font-bold text-slate-500 w-24">קטגוריה</th>
                                        <th className="p-3 text-xs font-bold text-slate-500 w-20">עובי (מ"מ)</th>
                                        <th className="p-3 text-xs font-bold text-slate-500 w-28">מחיר/מ"ר (₪)</th>
                                        <th className="p-3 text-xs font-bold text-slate-500 w-20">פעולות</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {items.map(item => (
                                        <tr key={item._id} className="border-b border-slate-100 hover:bg-slate-50/50 transition-colors">
                                            <td className="p-3 font-mono text-sm text-slate-500">{item.glassId}</td>
                                            <td className="p-3">
                                                <span className="font-bold text-slate-800 text-sm">{item.description}</span>
                                                {item.source === 'user' && (
                                                    <span className="mr-2 text-[9px] bg-violet-100 text-violet-600 px-1.5 py-0.5 rounded-full font-bold">משתמש</span>
                                                )}
                                            </td>
                                            <td className="p-3">
                                                <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${CATEGORY_COLORS[item.category] || 'bg-slate-100 text-slate-600 border-slate-200'}`}>
                                                    {item.category}
                                                </span>
                                            </td>
                                            <td className="p-3 font-mono text-sm text-center">{item.thickness || '—'}</td>
                                            <td className="p-3">
                                                {editingPrice === item._id ? (
                                                    <div className="flex items-center gap-1">
                                                        <input type="number" step="0.01" value={priceValue} onChange={e => setPriceValue(e.target.value)}
                                                            className="w-20 px-2 py-1 text-sm border border-cyan-300 rounded-lg focus:ring-2 focus:ring-cyan-500/20 outline-none font-mono" autoFocus
                                                            onKeyDown={e => { if (e.key === 'Enter') savePriceToDB(item); if (e.key === 'Escape') setEditingPrice(null); }} />
                                                        <button onClick={() => savePriceToDB(item)} className="text-[10px] bg-cyan-500 text-white px-1.5 py-1 rounded font-bold">✓</button>
                                                        <button onClick={() => setEditingPrice(null)} className="text-[10px] text-slate-400">✕</button>
                                                    </div>
                                                ) : (
                                                    <div className="flex items-center gap-1 cursor-pointer group" onClick={() => { setEditingPrice(item._id); setPriceValue(item.priceSqM || ''); }}>
                                                        {item.priceSqM > 0 ? (
                                                            <span className="font-bold text-emerald-600">{item.priceSqM} ₪</span>
                                                        ) : (
                                                            <span className="text-red-500 text-xs font-bold">חסר</span>
                                                        )}
                                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 text-slate-300 opacity-0 group-hover:opacity-100 transition-opacity" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" /></svg>
                                                    </div>
                                                )}
                                            </td>
                                            <td className="p-3">
                                                <div className="flex gap-1">
                                                    {canEdit(item) && (
                                                        <button onClick={() => openEditModal(item)} className="p-1 text-slate-400 hover:text-cyan-600 hover:bg-cyan-50 rounded transition-colors" title="עריכה">
                                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                                                        </button>
                                                    )}
                                                    {isAdmin && (
                                                        <button onClick={() => handleDelete(item)} className="p-1 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors" title="מחיקה">
                                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                                                        </button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}
            </div>

            {/* Add/Edit Modal */}
            {showModal && (
                <div className="fixed inset-0 z-50 bg-slate-900/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setShowModal(false)}>
                    <div className="bg-white rounded-2xl w-full max-w-lg shadow-2xl overflow-hidden" onClick={e => e.stopPropagation()}>
                        <div className="bg-cyan-50 border-b border-cyan-200 px-6 py-4 flex justify-between items-center">
                            <h3 className="font-black text-slate-800 text-lg">{editingItem ? 'עריכת סוג זכוכית' : 'הוספת סוג זכוכית'}</h3>
                            <button onClick={() => setShowModal(false)} className="text-slate-400 hover:text-slate-600"><svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg></button>
                        </div>
                        <div className="p-6 space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-bold text-slate-700 mb-1">קוד</label>
                                    <input type="number" value={formData.glassId} onChange={e => setFormData(f => ({ ...f, glassId: e.target.value }))} disabled={!!editingItem}
                                        className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-cyan-500/20 focus:border-cyan-500 outline-none disabled:bg-slate-100 font-mono" placeholder="חדש אוטומטי" />
                                </div>
                                <div>
                                    <label className="block text-sm font-bold text-slate-700 mb-1">עובי (מ"מ)</label>
                                    <input type="number" value={formData.thickness} onChange={e => setFormData(f => ({ ...f, thickness: e.target.value }))}
                                        className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-cyan-500/20 focus:border-cyan-500 outline-none" placeholder="6" />
                                </div>
                            </div>
                            <div>
                                <label className="block text-sm font-bold text-slate-700 mb-1">תיאור *</label>
                                <input type="text" value={formData.description} onChange={e => setFormData(f => ({ ...f, description: e.target.value }))}
                                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-cyan-500/20 focus:border-cyan-500 outline-none" placeholder='זכוכית מחוסמת שקופה 6 מ"מ' />
                            </div>
                            <div>
                                <label className="block text-sm font-bold text-slate-700 mb-1">מחיר למ"ר (₪)</label>
                                <input type="number" step="0.01" value={formData.priceSqM} onChange={e => setFormData(f => ({ ...f, priceSqM: e.target.value }))}
                                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-cyan-500/20 focus:border-cyan-500 outline-none font-mono" placeholder="110" />
                            </div>
                        </div>
                        <div className="bg-slate-50 border-t border-slate-200 px-6 py-4 flex justify-end gap-3">
                            <button onClick={() => setShowModal(false)} className="px-4 py-2 text-slate-600 hover:text-slate-800 font-bold text-sm">ביטול</button>
                            <button onClick={handleSave} disabled={saving} className="px-6 py-2 bg-cyan-600 hover:bg-cyan-700 text-white rounded-lg font-bold text-sm shadow-md disabled:opacity-50 transition-all">
                                {saving ? 'שומר...' : (editingItem ? 'שמור שינויים' : 'הוסף')}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
